export interface ManagementEntity {
  id: number
  status: string
}

/** 批量动作逐项处置结果（删年级/批量删班级等子树级联判定）。 */
export type BulkActionType = 'DELETED' | 'BLOCKED' | 'ARCHIVE_SUGGESTED' | 'SKIPPED'

export interface BulkActionItem {
  id: number | null
  name: string | null
  action: BulkActionType
  reason: string | null
  usageCounts?: Record<string, number>
}

export interface BulkActionResult {
  total: number
  deletedCount: number
  blockedCount: number
  archiveSuggestedCount: number
  items: BulkActionItem[]
}

export type DataManagementPageResponse<T> = {
  items: T[]
  total: number
  page: number
  pageSize: number
}

const STATUS_ENABLED = '启用'
const STATUS_DISABLED = '禁用'

export function createEmptyPageResponse<T>(): DataManagementPageResponse<T> {
  return {
    items: [],
    total: 0,
    page: 1,
    pageSize: 0
  }
}

export function expectArrayData<T>(data: unknown, endpoint: string): T[] {
  if (!Array.isArray(data)) {
    throw new Error(`Invalid response payload for ${endpoint}: expected array`)
  }
  return data as T[]
}

export function normalizeListResponse<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (payload && typeof payload === 'object') {
    const record = payload as { data?: unknown; items?: unknown }
    if (Array.isArray(record.data)) {
      return record.data as T[]
    }
    if (Array.isArray(record.items)) {
      return record.items as T[]
    }
  }

  return []
}

export function normalizePageResponse<T>(payload: unknown): DataManagementPageResponse<T> {
  if (Array.isArray(payload)) {
    return {
      items: payload as T[],
      total: payload.length,
      page: 1,
      pageSize: payload.length
    }
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Partial<DataManagementPageResponse<T>> & { content?: unknown }
    const items = Array.isArray(record.items)
      ? record.items
      : Array.isArray(record.content)
        ? (record.content as T[])
        : undefined

    if (items) {
      return {
        items,
        total: typeof record.total === 'number' ? record.total : items.length,
        page: typeof record.page === 'number' ? record.page : 1,
        pageSize: typeof record.pageSize === 'number' ? record.pageSize : items.length
      }
    }
  }

  return createEmptyPageResponse<T>()
}

export function normalizeStatusText(value: unknown, fallback = STATUS_ENABLED): string {
  if (typeof value === 'number') {
    return value === 1 ? STATUS_ENABLED : STATUS_DISABLED
  }

  const normalized = String(value ?? '').trim()
  if (!normalized) {
    return fallback
  }

  if (normalized === STATUS_ENABLED || normalized === STATUS_DISABLED) {
    return normalized
  }

  if (normalized === '归档' || normalized === '已归档') {
    return '归档'
  }

  return fallback
}

export function normalizeBoolean(value: unknown): boolean | undefined {
  if (value == null) {
    return undefined
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  const normalized = String(value).trim().toLowerCase()
  if (normalized === 'true' || normalized === '1') {
    return true
  }
  if (normalized === 'false' || normalized === '0' || normalized === '') {
    return false
  }
  return Boolean(value)
}

export function toTrimmedStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? '').trim())
      .filter((item) => item.length > 0)
  }

  return String(value ?? '')
    .split(/[\n,，、]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}
